import { createTheme } from '@material-ui/core/styles';
import { colors } from './colors';
import { typography } from './typography';
import variables from '../Sys/variable.scss';
import { DARK_TOKENS, LIGHT_TOKENS } from './colorMode';

const withModeTypography = (mode) => {
  const text = mode === 'dark' ? DARK_TOKENS.darkThemeText : colors.common.black;
  const muted = mode === 'dark' ? DARK_TOKENS.dmMuted : '#808080';
  const next = {
    ...typography,
    h1: { ...typography.h1, color: text },
    h2: { ...typography.h2, color: text },
    h4: { ...typography.h4, color: text },
    body1: { ...typography.body1, color: text },
    body2: { ...typography.body2, color: text },
    subtitle1: { ...typography.subtitle1, color: muted },
    subtitle2: { ...typography.subtitle2, color: muted },
  };
  return next;
};

export const createAppTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  const tokens = isDark ? DARK_TOKENS : LIGHT_TOKENS;
  const containedBg = isDark ? '#243044' : '#eef1f5';
  const containedHover = isDark ? '#2c3b56' : '#dfe5ec';
  const containedText = isDark ? tokens.darkThemeText : '#435166';
  const outlinedBorder = isDark ? tokens.dmBorder : '#c9d4e1';
  const outlinedText = isDark ? tokens.darkThemeText : '#4d5d70';
  const outlinedHover = isDark ? '#1a2740' : '#f5f7fa';
  const outlinedPrimaryHover = isDark ? 'rgba(61, 125, 221, 0.16)' : '#eef5fd';
  const outlinedBg = isDark ? 'transparent' : colors.common.white;
  const disabledBorder = isDark ? '#2a3850' : '#dce3eb';
  const disabledBg = isDark ? '#182234' : '#f8fafc';
  const disabledText = isDark ? '#6d7d93' : '#a0aab7';
  const switchTrack = isDark ? '#34445d' : '#c7d0dc';
  const switchTrackBorder = isDark ? '#52647f' : '#b7c2cf';
  const switchDisabledTrack = isDark ? '#222e42' : '#e6ebf1';
  const switchDisabledBorder = isDark ? '#34445d' : '#d5dde7';
  const switchDisabledThumb = isDark ? '#728097' : '#b8c2cf';
  const surfaceShadow = isDark ? '0 8px 24px rgba(0, 0, 0, 0.28)' : '0 8px 24px rgba(12, 41, 84, 0.055)';

  return createTheme({
    palette: {
      ...colors,
      type: isDark ? 'dark' : 'light',
      background: {
        default: tokens.dmPage,
        paper: tokens.contentColor,
      },
      text: {
        primary: isDark ? tokens.darkThemeText : colors.text.primary,
        secondary: isDark ? tokens.dmMuted : colors.text.secondary,
      },
    },
    typography: withModeTypography(mode),
    props: {
      MuiButton: {
        disableElevation: true,
      },
      MuiSwitch: {
        color: 'primary',
        disableRipple: true,
      },
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          body: {
            backgroundColor: tokens.dmPage,
            color: tokens.darkThemeText,
          },
        },
      },
      // MUI hardcodes lineHeight: 1 on form labels; with the Inter font that's too
      // tight and descenders (g, p, y, etc.) visually overlap the field's bottom
      // border/outline. Give labels a touch more line-height everywhere so text
      // isn't clipped, on both the resting and shrunk (floating) label states.
      MuiFormLabel: {
        root: {
          lineHeight: 1.3,
        },
      },
      MuiPaper: {
        ...(isDark ? {
          root: {
            backgroundColor: tokens.contentColor,
            color: tokens.darkThemeText,
          },
        } : {}),
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: surfaceShadow,
        },
      },
      MuiCard: {
        root: {
          borderRadius: 16,
          border: `1px solid ${tokens.dmBorder}`,
          boxShadow: surfaceShadow,
        },
      },
      MuiChip: {
        root: {
          fontWeight: 600,
          backgroundColor: isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(100, 116, 139, 0.12)',
          color: isDark ? tokens.dmTextStrong : '#475569',
        },
        sizeSmall: {
          fontSize: '0.72rem',
        },
      },
      MuiLinearProgress: {
        root: {
          borderRadius: 999,
        },
        bar: {
          borderRadius: 999,
        },
      },
      MuiTabs: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
      MuiTab: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
      MuiButton: {
        root: {
          minHeight: 40,
          borderRadius: '8px !important',
          padding: '8px 16px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.8125rem',
          fontWeight: 600,
          lineHeight: 1.5,
          letterSpacing: 0,
          textTransform: 'none',
          boxShadow: 'none !important',
          transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
          '&:focus-visible': {
            outline: `3px solid ${colors.primary.main}33`,
            outlineOffset: 2,
          },
          '&.Mui-disabled': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: containedBg,
          color: containedText,
          '&:hover': {
            backgroundColor: containedHover,
          },
        },
        containedPrimary: {
          backgroundColor: colors.primary.main,
          color: colors.common.white,
          '&:hover': {
            backgroundColor: colors.primary.dark,
          },
        },
        containedSecondary: {
          backgroundColor: colors.secondary.main,
          color: colors.common.white,
          '&:hover': {
            backgroundColor: colors.secondary.light,
          },
        },
        outlined: {
          borderColor: `${outlinedBorder} !important`,
          backgroundColor: `${outlinedBg} !important`,
          color: `${outlinedText} !important`,
          '&:hover': {
            borderColor: `${isDark ? '#4d6484' : '#9eacbc'} !important`,
            backgroundColor: `${outlinedHover} !important`,
          },
          '&.Mui-disabled': {
            borderColor: `${disabledBorder} !important`,
            backgroundColor: `${disabledBg} !important`,
            color: `${disabledText} !important`,
          },
        },
        outlinedPrimary: {
          borderColor: `${colors.primary.main} !important`,
          backgroundColor: `${outlinedBg} !important`,
          color: `${isDark ? '#9ec2ff' : colors.primary.main} !important`,
          '&:hover': {
            borderColor: `${colors.primary.dark} !important`,
            backgroundColor: `${outlinedPrimaryHover} !important`,
            color: `${isDark ? '#c5daff' : colors.primary.dark} !important`,
          },
          '&.Mui-disabled': {
            borderColor: `${disabledBorder} !important`,
            backgroundColor: `${disabledBg} !important`,
            color: `${disabledText} !important`,
          },
        },
        outlinedSecondary: {
          borderColor: `${colors.secondary.main} !important`,
          backgroundColor: `${outlinedBg} !important`,
          color: `${colors.secondary.main} !important`,
          '&:hover': {
            backgroundColor: `${isDark ? 'rgba(236, 122, 18, 0.12)' : '#fff6ed'} !important`,
          },
        },
        textPrimary: {
          color: colors.primary.main,
          '&:hover': {
            backgroundColor: outlinedPrimaryHover,
          },
          '&.Mui-disabled': {
            color: disabledText,
          },
        },
        sizeSmall: {
          minHeight: 32,
          padding: '5px 10px',
          fontSize: '0.75rem',
        },
        sizeLarge: {
          minHeight: 44,
          padding: '10px 20px',
          fontSize: '0.875rem',
        },
      },
      MuiIconButton: {
        colorPrimary: {
          background: colors.primary.main,
          color: colors.common.white,
          '&:hover': {
            backgroundColor: colors.common.black,
            color: colors.common.white,
          },
          '&.MuiCheckbox-root, &.MuiRadio-root': {
            background: 'transparent',
            color: isDark ? tokens.dmMuted : '#8a97a8',
            '&.Mui-checked, &.MuiCheckbox-indeterminate': {
              color: colors.primary.main,
            },
            '&:hover': {
              backgroundColor: 'rgba(61, 125, 221, 0.08)',
            },
            '&.Mui-disabled': {
              color: disabledText,
            },
          },
        },
        colorSecondary: {
          backgroundColor: isDark ? tokens.contentColor : colors.common.white,
          color: colors.grey[400],
        },
        sizeSmall: {
          color: colors.grey[400],
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiSelect: {
        icon: {
          color: isDark ? tokens.dmMuted : '#647184',
        },
        select: {
          borderRadius: 8,
          '&:focus': {
            backgroundColor: 'transparent',
            borderRadius: 8,
          },
        },
        outlined: {
          borderColor: isDark ? tokens.dmBorder : colors.grey[200],
        },
      },
      MuiMenu: {
        paper: {
          marginTop: 6,
          borderRadius: 10,
          border: `1px solid ${isDark ? tokens.dmBorder : '#e7ebf0'}`,
          backgroundColor: isDark ? tokens.contentColor : '#fff',
          boxShadow: isDark
            ? '0 12px 32px rgba(0, 0, 0, 0.35)'
            : '0 8px 24px rgba(12, 41, 84, 0.1)',
          '&.MuiPaper-elevation8': {
            boxShadow: isDark
              ? '0 12px 32px rgba(0, 0, 0, 0.35)'
              : '0 8px 24px rgba(12, 41, 84, 0.1)',
          },
        },
        list: {
          padding: 6,
        },
      },
      MuiAutocomplete: {
        paper: {
          marginTop: 6,
          borderRadius: 10,
          border: `1px solid ${isDark ? tokens.dmBorder : '#e7ebf0'}`,
          backgroundColor: isDark ? tokens.contentColor : '#fff',
          boxShadow: isDark
            ? '0 12px 32px rgba(0, 0, 0, 0.35)'
            : '0 8px 24px rgba(12, 41, 84, 0.1)',
        },
        listbox: {
          padding: 6,
        },
        option: {
          minHeight: 36,
          borderRadius: 6,
          marginTop: 2,
          marginBottom: 2,
          paddingTop: 7,
          paddingBottom: 7,
          paddingLeft: 10,
          paddingRight: 10,
          fontSize: '0.8125rem',
          fontWeight: 500,
          lineHeight: 1.35,
          color: isDark ? tokens.darkThemeText : '#263548',
          '&[data-focus="true"]': {
            backgroundColor: isDark ? 'rgba(61, 125, 221, 0.22)' : 'rgba(61, 125, 221, 0.08)',
          },
          '&[aria-selected="true"]': {
            backgroundColor: isDark ? 'rgba(61, 125, 221, 0.28)' : 'rgba(61, 125, 221, 0.12)',
          },
        },
      },
      MuiMenuItem: {
        root: {
          minHeight: 36,
          borderRadius: 6,
          marginTop: 2,
          marginBottom: 2,
          paddingTop: 7,
          paddingBottom: 7,
          paddingLeft: 10,
          paddingRight: 10,
          fontSize: '0.8125rem',
          fontWeight: 500,
          lineHeight: 1.35,
          color: isDark ? tokens.darkThemeText : '#263548',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(61, 125, 221, 0.22)' : 'rgba(61, 125, 221, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: isDark ? 'rgba(61, 125, 221, 0.28)' : 'rgba(61, 125, 221, 0.12)',
            color: isDark ? '#fff' : '#263548',
          },
          '&.Mui-selected:hover': {
            backgroundColor: isDark ? 'rgba(61, 125, 221, 0.34)' : 'rgba(61, 125, 221, 0.16)',
          },
        },
      },
      MuiDivider: {
        root: {
          backgroundColor: isDark ? tokens.dmBorder : colors.grey[200],
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: 10,
          ...(isDark ? {
            backgroundColor: tokens.dmInput,
            color: tokens.darkThemeText,
          } : {}),
        },
        notchedOutline: {
          borderColor: isDark ? tokens.dmBorder : colors.grey[200],
        },
      },
      ...(isDark ? {
        MuiInputBase: {
          root: {
            color: tokens.darkThemeText,
          },
          input: {
            color: tokens.darkThemeText,
          },
        },
        MuiInputLabel: {
          root: {
            color: tokens.dmMuted,
          },
        },
        MuiFormLabel: {
          root: {
            lineHeight: 1.3,
            color: tokens.dmTextStrong,
          },
        },
      } : {}),
      MuiDialogActions: {
        root: {
          borderTopColor: isDark ? tokens.dmBorder : colors.grey[200],
        },
      },
      MuiDialog: {
        paper: {
          borderRadius: 16,
          ...(isDark ? {
            backgroundColor: tokens.contentColor,
            color: tokens.darkThemeText,
          } : {}),
        },
      },
      MuiRadio: {
        root: {
          borderColor: isDark ? tokens.dmBorder : colors.grey[200],
        },
      },
      MuiSwitch: {
        root: {
          width: 46,
          height: 26,
          padding: 0,
          margin: '0 8px',
          overflow: 'visible',
        },
        switchBase: {
          padding: 3,
          color: '#ffffff',
          transition: 'transform 180ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(61, 125, 221, 0.08)',
          },
          '&$checked': {
            transform: 'translateX(20px)',
            color: '#ffffff',
          },
          '&$disabled': {
            color: switchDisabledThumb,
          },
          '&$disabled + $track': {
            opacity: 1,
            backgroundColor: switchDisabledTrack,
            borderColor: switchDisabledBorder,
          },
          '&.Mui-focusVisible $thumb': {
            boxShadow: `0 0 0 4px ${isDark ? 'rgba(94, 196, 232, 0.24)' : 'rgba(61, 125, 221, 0.22)'}`,
          },
        },
        colorPrimary: {
          '&$checked': {
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(61, 125, 221, 0.12)',
            },
          },
          '&$checked + $track': {
            opacity: 1,
            backgroundColor: colors.primary.main,
            borderColor: colors.primary.main,
          },
        },
        colorSecondary: {
          '&$checked': {
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(61, 125, 221, 0.12)',
            },
          },
          '&$checked + $track': {
            opacity: 1,
            backgroundColor: colors.primary.main,
            borderColor: colors.primary.main,
          },
        },
        thumb: {
          width: 20,
          height: 20,
          boxShadow: isDark
            ? '0 2px 6px rgba(0, 0, 0, 0.42)'
            : '0 2px 6px rgba(38, 53, 72, 0.28)',
          transition: 'box-shadow 180ms ease',
        },
        track: {
          borderRadius: 13,
          opacity: 1,
          backgroundColor: switchTrack,
          border: `1px solid ${switchTrackBorder}`,
          boxSizing: 'border-box',
          transition: 'background-color 180ms ease, border-color 180ms ease',
        },
        sizeSmall: {
          width: 38,
          height: 22,
          padding: 0,
          '& $thumb': {
            width: 18,
            height: 18,
          },
          '& $switchBase': {
            padding: 2,
            '&$checked': {
              transform: 'translateX(16px)',
            },
          },
        },
      },
      MuiInput: {
        root: {
          borderColor: isDark ? tokens.dmBorder : '#838383',
          ...(isDark ? { color: tokens.darkThemeText } : {}),
        },
      },
      MuiBadge: {
        colorSecondary: {
          color: '#fff',
        },
      },
      MuiTableHead: {
        root: {
          backgroundColor: tokens.dmRowAlt,
        },
      },
      MuiTableCell: {
        root: {
          borderBottomColor: tokens.dmBorder,
        },
        head: {
          color: tokens.dmTextStrong,
          backgroundColor: tokens.dmRowAlt,
          fontWeight: 700,
        },
        stickyHeader: {
          backgroundColor: tokens.dmRowAlt,
        },
        ...(isDark ? {
          body: {
            color: tokens.dmTextStrong,
          },
        } : {}),
      },
      MuiPaginationItem: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          ...(isDark ? { color: tokens.darkThemeText } : {}),
        },
        outlined: {
          borderColor: tokens.dmBorder,
        },
        page: {
          '&.Mui-selected': {
            backgroundColor: variables.primaryColor,
            color: '#fff',
            borderColor: variables.primaryColor,
            '&:hover': {
              backgroundColor: variables.primaryColor,
            },
          },
        },
      },
    },
  });
};

export const muiTheme = createAppTheme('light');

export default muiTheme;
